/**
 * Display helpers for the bespoke reports.
 *
 * Transplanted from the reference implementation's `lib/format.ts`. The portal
 * has its own `lib/format.ts` for the generic renderer; these are kept separate
 * rather than merged, because the two answer different questions — the portal's
 * formats a `ColumnSpec.kind`, these format the specific fields these reports
 * show.
 */

const MONTH_NAMES = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
]

/** "2026-07" -> "Jul 2026". A value that is not a month key is returned as-is. */
export function monthLabel(month: string): string {
  const [year, m] = month.split('-')
  const name = MONTH_NAMES[Number(m) - 1]
  if (name === undefined || year === undefined) return month
  return `${name} ${year}`
}

/** "2026-07" -> "Jul" — for dense axes where the year is in the header. */
export function monthShort(month: string): string {
  return MONTH_NAMES[Number(month.split('-')[1]) - 1] ?? month
}

export function formatDate(value: string | null): string {
  if (value === null) return ''
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return ''
  return d.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

/**
 * Text as it should appear in the UI.
 *
 * NULL from the database arrives as null and renders blank. The literal string
 * "None" would render as written — it is a value the source system has produced
 * before, and although it does not occur today (measured at P2a), the handling
 * stays: these views are not ours, and the string returns the moment someone
 * rebuilds the pivot with Python string formatting.
 */
export function displayText(value: string | null | undefined): string {
  return value ?? ''
}

/** First line of a long field, for the collapsed table row. */
export function summarize(value: string | null | undefined, max = 90): string {
  const text = displayText(value).replace(/\s+/g, ' ').trim()
  if (text.length <= max) return text
  return `${text.slice(0, max).trimEnd()}…`
}

/**
 * Map a CAR status to a status-palette role. Open work is what a reader needs to
 * spot, so closed states stay neutral and in-flight states carry the color.
 */
export function statusRole(
  status: string | null,
): 'good' | 'warning' | 'serious' | 'critical' | 'neutral' {
  if (status === null) return 'neutral'
  const s = status.toLowerCase()
  if (s.includes('hold')) return 'critical'
  if (s.includes('closed') || s.includes('accepted')) return 'good'
  if (s.includes('pending') || s.includes('ready for')) return 'warning'
  if (s.includes('sent to supplier')) return 'serious'
  return 'neutral'
}
